import { pairingApi, tvApi, type CredentialOwner } from "../api/TvApi";
import { allowsAutomaticGallerySpotlight, crowdSceneNeedsReset, MojVibeDirector } from "../core/director/MojVibeDirector";
import { SceneOrchestrator } from "../core/director/SceneOrchestrator";
import { priorities, type PresentationRequest } from "../core/director/types";
import { timing } from "../core/director/timing";
import { AssetPreloader } from "../core/media/AssetPreloader";
import { effectFromCue } from "../core/effects/CueEffectAdapter";
import { getEffectPlaybackDurationMs, shouldPersistEffect } from "../core/effects/effectTiming";
import { PartyRealtimeClient } from "../core/realtime/PartyRealtimeClient";
import { ClockSynchronizer } from "../core/sync/ClockSynchronizer";
import { RevisionTracker } from "../core/sync/RevisionTracker";
import { reconcileSnapshot } from "../core/sync/StateReconciler";
import { logger } from "../core/telemetry/logger";
import type { GuestWallContribution, PartyCueTriggered, PartyStyle, RoomEffect, ScreenCredentials, TvPresentationCommand } from "../models/contracts";
import { session } from "../state/session";
import { tvStore } from "../state/TvStore";
import { parseLyrics, type LyricLine } from "../utilities/lyrics";
import { MemoryStageController } from "../memory/MemoryStageController";

class Credentials implements CredentialOwner {
  private refreshing: Promise<ScreenCredentials> | null = null;
  constructor(private value: ScreenCredentials) {}
  get = () => this.value;
  async refresh(): Promise<ScreenCredentials> { if (!this.refreshing) this.refreshing = pairingApi.refresh(this.value).then(value => { this.value = value; session.save(value); tvStore.patch({ credentials: value }); return value; }).finally(() => { this.refreshing = null; }); return this.refreshing; }
}

export class LiveStageRuntime {
  private stopped = false; private owner: Credentials | null = null; private realtime: PartyRealtimeClient | null = null;
  private syncAbort: AbortController | null = null; private syncInFlight: Promise<void> | null = null;
  private director = new MojVibeDirector(); private orchestrator = new SceneOrchestrator(); private clock = new ClockSynchronizer(); private revision = new RevisionTracker(); private media = new AssetPreloader();
  private tickTimer: number | null = null; private memoryTimer: number | null = null; private eventIds = new Set<string>();
  private lockedEffectIds = new Set<string>();
  private effectDelivery: Promise<void> = Promise.resolve(); private effectOrder = 0;
  private lyricsTrackId: string | null = null;
  private lyricsLines: LyricLine[] = [];
  private memory = new MemoryStageController();
  private hostMemoryUntil = 0;
  private partyStyle: PartyStyle | null = null;
  private partyStyleLoadedAt = 0;
  private partyStyleRequest: Promise<PartyStyle | null> | null = null;

  async start(): Promise<void> {
    this.stopped = false; window.addEventListener("mojvibe:scene-failed", this.sceneFailed); window.addEventListener("mojvibe:memory-media-failed", this.memoryMediaFailed as EventListener); window.addEventListener("mojvibe:dismiss-join", this.dismissJoin); this.startTimers(); const restored = session.load();
    if (restored) { try { await this.connect(restored, true); return; } catch (error) { logger.warn("TV_SESSION_RESTORE_FAILED", { message: error instanceof Error ? error.message : "unknown" }); session.clear(); } }
    await this.pair();
  }
  async pairAgain(): Promise<void> { await this.shutdown(); session.clear(); this.stopped = false; this.startTimers(); await this.pair(); }
  async retry(): Promise<void> { if (this.owner) await this.sync(true).catch(error => tvStore.patch({ error: error instanceof Error ? error.message : "Unable to reconnect" })); else await this.pair(); }
  async stop(): Promise<void> { this.stopped = true; window.removeEventListener("mojvibe:scene-failed", this.sceneFailed); window.removeEventListener("mojvibe:memory-media-failed", this.memoryMediaFailed as EventListener); window.removeEventListener("mojvibe:dismiss-join", this.dismissJoin); await this.shutdown(); }

  private async pair(): Promise<void> {
    tvStore.pairing();
    while (!this.stopped) {
      try {
        const created = await pairingApi.create(); tvStore.pairing(created);
        while (!this.stopped && Date.parse(created.expiresAt) > Date.now()) {
          await new Promise(resolve => window.setTimeout(resolve, Math.max(1, created.pollAfterSeconds) * 1000));
          const status = await pairingApi.status(created.pairingSessionId, created.clientSecret);
          if (status.status === "Approved") { const credentials = await pairingApi.exchange(created.pairingSessionId, created.clientSecret); session.save(credentials); await this.connect(credentials, false); return; }
          if (["Expired", "Revoked", "Consumed"].includes(status.status)) break;
        }
      } catch (error) { if (this.stopped) return; tvStore.patch({ error: error instanceof Error ? error.message : "Pairing unavailable" }); await new Promise(resolve => window.setTimeout(resolve, 3_000)); }
    }
  }

  private async connect(credentials: ScreenCredentials, restore: boolean): Promise<void> {
    tvStore.patch({ phase: "booting", credentials, connection: "connecting", error: null }); this.owner = new Credentials(credentials);
    if (restore && Date.parse(credentials.accessTokenExpiresAt) < Date.now() + 45_000) await this.owner.refresh();
    await this.sync(true);
    this.realtime = new PartyRealtimeClient(this.owner, {
      assignment: value => tvStore.patch({ assignment: value }),
      changed: (_name, message) => this.onChanged(message),
      effect: value => this.enqueueEffect(value), cue: value => this.onCue(value), wall: value => this.onWallContribution(value), presentation: value => void this.onPresentation(value),
      state: value => { const reconnecting = value !== "connected"; tvStore.patch({ connection: value === "closed" ? "offline" : value, reconnectStartedAt: reconnecting ? tvStore.get().reconnectStartedAt ?? Date.now() : null }); logger.info(`REALTIME_${value.toUpperCase()}`); },
      terminal: () => void this.pairAgain(), reconnected: () => this.sync(true),
    });
    await this.realtime.start(); tvStore.patch({ phase: "stage", connection: "connected", reconnectStartedAt: null });
    logger.info("TV_CONNECTED", { roomId: credentials.roomId, screenId: credentials.screenId });
  }

  private onChanged(message: any): void {
    const incoming = typeof message?.revision === "number" ? message.revision : null;
    if (incoming !== null && this.revision.classify(incoming) === "duplicate") return;
    if (incoming !== null && this.revision.classify(incoming) === "gap") logger.warn("REVISION_GAP", { current: this.revision.current, incoming });
    void this.sync(incoming !== null && this.revision.classify(incoming) === "gap");
  }

  private async sync(force = false): Promise<void> {
    if (!this.owner || this.stopped) return;
    if (this.syncInFlight && !force) return this.syncInFlight;
    if (force) this.syncAbort?.abort(); const abort = new AbortController(); this.syncAbort = abort;
    const owner = this.owner;
    const task = tvApi.snapshot(owner, abort.signal).then(async snapshot => {
      if (abort.signal.aborted) return; const old = tvStore.get().snapshot; const next = reconcileSnapshot(old, snapshot);
      // Party Style is intentionally read through the existing TV-safe public
      // endpoint. Keep the latest value if an older backend or a transient
      // media URL failure makes the optional request unavailable.
      const partyStyle = await this.loadPartyStyle(owner);
      const enriched = { ...next, partyStyle: partyStyle ?? next.partyStyle ?? old?.partyStyle ?? null };
      this.clock.observe(next.serverTimeUtc); this.revision.rebase(next.revision);
      const previousTvSettings = old?.tvSettings;
      const displayPolicyChanged = Boolean(previousTvSettings) && ([
        "enabled", "automaticDirectorEnabled", "musicEnabled", "effectsEnabled", "pollsEnabled",
        "galleryEnabled", "wishWallEnabled", "timeCapsuleEnabled", "crowdWallCompanionEnabled", "joinQrEnabled",
      ] as const).some(key => previousTvSettings![key] !== enriched.tvSettings[key]);
      tvStore.patch({ phase: "stage", snapshot: enriched, assignment: enriched.screen, clockOffsetMs: this.clock.offsetMs, error: null });
      if (displayPolicyChanged) { this.orchestrator.current = null; this.orchestrator.queue.clear(); }
      if (!enriched.tvSettings.enabled) { this.orchestrator.current = null; this.orchestrator.queue.clear(); }
      this.media.preload([enriched.partyStyle?.selectedImageUrl, enriched.hostPlayback?.artworkUrl, ...enriched.tvPublicContent.gallery.slice(0, 6).flatMap(x => [x.thumbnailUrl, x.url]), ...enriched.tvPublicContent.wishes.slice(0, 3).map(x => x.photoUrl)]);
      for (const candidate of this.director.evaluate({ party: enriched, currentScene: this.orchestrator.current, history: this.orchestrator.history, now: this.clock.now(), lastSnapshot: old })) this.orchestrator.submit(candidate, this.clock.now());
      this.updateScene(); logger.info("SNAPSHOT_LOADED", { roomId: next.roomId, revision: next.revision });
      void this.loadLyrics(enriched);
    }).catch(error => { if (abort.signal.aborted) return; logger.error("SNAPSHOT_FAILED", { message: error instanceof Error ? error.message : "unknown" }); if (!tvStore.get().snapshot) throw error; }).finally(() => { if (this.syncAbort === abort) this.syncAbort = null; if (this.syncInFlight === task) this.syncInFlight = null; });
    this.syncInFlight = task; return task;
  }
  private async loadPartyStyle(owner: CredentialOwner): Promise<PartyStyle | null> {
    if (this.partyStyleLoadedAt && Date.now() - this.partyStyleLoadedAt < 60_000) return this.partyStyle;
    if (this.partyStyleRequest) return this.partyStyleRequest;
    this.partyStyleRequest = tvApi.selectedPartyStyle(owner).then(value => {
      this.partyStyle = value;
      this.partyStyleLoadedAt = Date.now();
      return value;
    }).catch(() => {
      this.partyStyleLoadedAt = Date.now();
      return this.partyStyle;
    }).finally(() => { this.partyStyleRequest = null; });
    return this.partyStyleRequest;
  }

  private async onEffect(effect: RoomEffect): Promise<void> {
    if (effect.effectType === "Lock") {
      this.applyEffectLock(effect);
      return;
    }
    if (effect.effectType === "Stop") {
      this.lockedEffectIds.clear();
      if (this.orchestrator.current?.request.type === "effect") {
        this.orchestrator.current = null;
        this.updateScene();
      }
      return;
    }
    const persistentByDefault = shouldPersistEffect(effect.playbackAudience);
    if (this.eventIds.has(effect.eventId) || (!persistentByDefault && Date.parse(effect.expiresAtUtc) <= this.clock.now())) return;
    this.rememberEvent(effect.eventId); const snapshot = tvStore.get().snapshot;
    if (!snapshot?.tvSettings.enabled || !snapshot.tvSettings.effectsEnabled || !this.owner) return;
    try {
      const playbackUrl = await tvApi.effectUrl(this.owner, effect);
      const startAt = Date.parse(effect.scheduledStartUtc);
      const delay = Math.max(0, startAt - this.clock.now());
      const createdAt = Date.parse(effect.serverTime) + (++this.effectOrder / 1_000);
      window.setTimeout(() => {
        const locked = persistentByDefault || this.lockedEffectIds.has(effect.effectId);
        if (this.stopped || (!locked && this.clock.now() > Date.parse(effect.expiresAtUtc))) return;
        this.orchestrator.submit({
          id: `effect:${effect.eventId}`,
          type: "effect",
          kind: "fullscreen-media",
          priority: priorities.effect,
          createdAt,
          durationMs: locked ? undefined : getEffectPlaybackDurationMs(effect.durationMs),
          expiresAt: locked ? undefined : Date.parse(effect.expiresAtUtc),
          dedupeKey: effect.eventId,
          payload: { ...effect, effectMode: locked ? "lock" : effect.effectMode, playbackUrl },
        }, this.clock.now());
        this.updateScene();
      }, delay);
    } catch (error) { logger.error("EFFECT_SKIPPED", { effectId: effect.effectId, message: error instanceof Error ? error.message : "unavailable" }); }
  }

  private applyEffectLock(effect: RoomEffect): void {
    if (this.eventIds.has(effect.eventId)) return;
    this.rememberEvent(effect.eventId);
    const locked = effect.effectMode.toLowerCase() === "lock";
    if (locked) this.lockedEffectIds.add(effect.effectId);
    else this.lockedEffectIds.delete(effect.effectId);

    const current = this.orchestrator.current;
    if (!current || current.request.type !== "effect") return;
    const currentEffect = current.request.payload as RoomEffect;
    if (currentEffect.effectId !== effect.effectId) return;

    current.request = {
      ...current.request,
      durationMs: locked ? undefined : getEffectPlaybackDurationMs(currentEffect.durationMs),
      expiresAt: locked ? undefined : Date.parse(currentEffect.expiresAtUtc),
      payload: { ...currentEffect, effectMode: locked ? "lock" : "animation" },
    };
    current.endsAt = locked
      ? undefined
      : current.startedAt + getEffectPlaybackDurationMs(currentEffect.durationMs);
    this.updateScene();
  }

  private onCue(cue: PartyCueTriggered): void {
    const effect = effectFromCue(cue);
    if (effect) this.enqueueEffect(effect);
  }

  private onWallContribution(message: GuestWallContribution): void {
    const snapshot = tvStore.get().snapshot;
    const crowd = snapshot?.crowdScreen;
    if (!snapshot || !crowd || message.roomId !== snapshot.roomId || message.sessionId !== crowd.id
      || message.mode !== crowd.mode || crowd.status !== "Live" || !snapshot.tvSettings.crowdWallCompanionEnabled) return;
    const createdAt = message.serverTimeMs || Date.parse(message.serverTime);
    if (message.mode === "TextWall") {
      this.orchestrator.submit({ id: `guest-wall:${message.participantId}:${createdAt}`, type: "moment", kind: "fullscreen-media",
        priority: priorities.effect, createdAt, durationMs: 5_000,
        dedupeKey: `guest-wall:${message.participantId}:${createdAt}`, payload: { current: message.value } }, this.clock.now());
    } else {
      const nextCrowd = { ...crowd, guestContributionAt: message.serverTime,
        emojiValue: message.mode === "EmojiWall" ? message.value : crowd.emojiValue,
        colorValue: message.mode === "ColorWall" ? message.value : crowd.colorValue };
      const nextSnapshot = { ...snapshot, crowdScreen: nextCrowd };
      this.orchestrator.submit({ id: `guest-wall:${message.participantId}:${createdAt}`, type: "crowd", kind: "fullscreen-media",
        priority: priorities.effect, createdAt, durationMs: 5_000,
        dedupeKey: `guest-wall:${message.participantId}:${createdAt}`, payload: nextSnapshot }, this.clock.now());
    }
    this.updateScene();
  }

  private enqueueEffect(effect: RoomEffect): void {
    this.effectDelivery = this.effectDelivery
      .then(() => this.onEffect(effect))
      .catch(error => logger.error("EFFECT_DELIVERY_FAILED", { effectId: effect.effectId, message: error instanceof Error ? error.message : "unavailable" }));
  }

  private async onPresentation(command: TvPresentationCommand): Promise<void> {
    if (this.eventIds.has(command.eventId) || Date.parse(command.expiresAt) <= this.clock.now()) return;
    if (command.type === "DismissJoinQr") {
      this.rememberEvent(command.eventId);
      if (this.orchestrator.current?.request.type === "join") this.orchestrator.current = null;
      this.updateScene();
      return;
    }
    if (command.type !== "ShowJoinQr") await this.sync(true);
    const snapshot = tvStore.get().snapshot; if (!snapshot?.tvSettings.enabled) return; const payload: unknown = snapshot; const type: PresentationRequest["type"] = "join";
    if (command.type === "GalleryHighlight" || command.type === "WishHighlight") {
      const memoryType = command.type === "GalleryHighlight" ? "gallery" : "wish";
      if (memoryType === "gallery" && !snapshot.tvSettings.galleryEnabled) return;
      if (memoryType === "wish" && !snapshot.tvSettings.wishWallEnabled) return;
      const items = memoryType === "gallery" ? snapshot.tvPublicContent.gallery : snapshot.tvPublicContent.wishes;
      if (!command.resourceId || !items.some(item => item.id === command.resourceId)) return;
      this.rememberEvent(command.eventId);
      const now = this.clock.now();
      this.memory.highlight(memoryType, command.resourceId, now, Date.parse(command.expiresAt));
      this.hostMemoryUntil = Math.min(Date.parse(command.expiresAt), now + (memoryType === "gallery" ? timing.gallerySpotlightMs : timing.wishSpotlightMs));
      const current = this.orchestrator.current;
      if (!current || (current.request.priority < priorities.highlighted && !["ceremony", "fullscreen-media", "interactive"].includes(current.request.kind))) this.orchestrator.current = null;
      this.updateScene(); return;
    }
    this.rememberEvent(command.eventId);
    this.orchestrator.submit({ id: command.eventId, type, kind: "spotlight", priority: command.priority, createdAt: Date.parse(command.serverTimestamp), expiresAt: Date.parse(command.expiresAt), durationMs: type === "join" ? timing.joinSpotlightMs : type === "wish" ? timing.wishSpotlightMs : timing.gallerySpotlightMs, dedupeKey: command.eventId, payload }, this.clock.now()); this.updateScene();
  }

  private startTimers(): void { if (this.tickTimer === null) this.tickTimer = window.setInterval(() => this.updateScene(), 500); if (this.memoryTimer === null) this.memoryTimer = window.setInterval(() => { const party = tvStore.get().snapshot; if (!party) return; const candidate = this.director.automaticMemory({ party, currentScene: this.orchestrator.current, history: this.orchestrator.history, now: this.clock.now() }); if (candidate) { this.orchestrator.submit(candidate, this.clock.now()); this.orchestrator.history.push({ key: "automatic-memory", shownAt: this.clock.now() }); this.updateScene(); } }, 4_000); }
  private async loadLyrics(party: NonNullable<ReturnType<typeof tvStore.get>["snapshot"]>): Promise<void> {
    const item = party.playlist?.items.find(value => value.id === party.hostPlayback?.playlistItemId);
    const trackId = item?.track?.id;
    if (!party.tvSettings.enabled || !party.hostPlayback || !party.tvSettings.musicEnabled || !trackId || !item?.track?.hasLyrics) {
      this.lyricsTrackId = null;
      this.lyricsLines = [];
      if (this.orchestrator.current?.request.type === "lyrics") this.orchestrator.current = null;
      return;
    }
    if (this.lyricsTrackId === trackId || !this.owner) return; this.lyricsTrackId = trackId;
    const kind = item.track.lyricsType === "Synced" ? "lrc" : "plain";
    const text = await tvApi.lyrics(this.owner, trackId, kind).catch(() => "");
    if (!text || this.lyricsTrackId !== trackId) return;
    this.lyricsLines = parseLyrics(text);
    if (this.director.ambient(party, this.clock.now()).type !== "memory")
      this.orchestrator.submit({ id: `lyrics:${trackId}`, type: "lyrics", kind: "spotlight", priority: priorities.passiveMusic + 5, createdAt: this.clock.now(), dedupeKey: `lyrics:${trackId}`, payload: { party, lines: this.lyricsLines } }, this.clock.now());
    this.updateScene();
  }
  private updateScene(): void {
    const party = tvStore.get().snapshot; if (!party) return; const now = this.clock.now();
    if (crowdSceneNeedsReset(this.orchestrator.current, party.crowdScreen)) this.orchestrator.current = null;
    if (!allowsAutomaticGallerySpotlight(party) && this.orchestrator.current?.request.type === "gallery") this.orchestrator.current = null;
    const wasMemory = this.orchestrator.current?.request.type === "memory";
    const forceHostMemory = now < this.hostMemoryUntil;
    let view = this.memory.reconcile(party, now, wasMemory || forceHostMemory);
    let ambient = this.director.ambient(party, now);
    if (forceHostMemory && view) ambient = { id: "memory:host-highlight", type: "memory", kind: "ambient", priority: priorities.ambient, createdAt: now, resumable: true, payload: view };
    if (ambient.type === "memory" && this.orchestrator.current?.request.type === "lyrics") this.orchestrator.current = null;
    if (ambient.type === "memory" && view) ambient = { ...ambient, payload: { ...view, lyrics: this.lyricsLines } };
    let scene = this.orchestrator.tick(ambient, now);
    const isMemory = scene.request.type === "memory";
    if (isMemory !== wasMemory) {
      view = this.memory.reconcile(party, now, isMemory);
      if (isMemory && view) { ambient = { ...ambient, payload: { ...view, lyrics: this.lyricsLines } }; scene = this.orchestrator.tick(ambient, now); }
    }
    tvStore.patch({ scene, queueLength: this.orchestrator.queue.length });
  }
  private rememberEvent(id: string): void { this.eventIds.add(id); if (this.eventIds.size > 128) this.eventIds.delete(this.eventIds.values().next().value as string); }
  private sceneFailed = () => { logger.warn("SCENE_MEDIA_FAILED", { scene: this.orchestrator.current?.request.type }); this.orchestrator.current = null; this.updateScene(); };
  private dismissJoin = () => { if (this.orchestrator.current?.request.type !== "join") return; this.orchestrator.current = null; this.updateScene(); };
  private memoryMediaFailed = (event: CustomEvent<{ type: "gallery" | "wish"; id: string }>) => { const detail = event.detail; if (!detail) return; logger.warn("MEMORY_MEDIA_SKIPPED", detail); this.memory.dismiss(detail.type, detail.id, this.clock.now()); this.updateScene(); };
  private async shutdown(): Promise<void> { this.syncAbort?.abort(); this.syncAbort = null; if (this.tickTimer !== null) window.clearInterval(this.tickTimer); if (this.memoryTimer !== null) window.clearInterval(this.memoryTimer); this.tickTimer = this.memoryTimer = null; await this.realtime?.stop().catch(() => undefined); this.realtime = null; this.owner = null; this.partyStyle = null; this.partyStyleLoadedAt = 0; this.partyStyleRequest = null; this.hostMemoryUntil = 0; this.lockedEffectIds.clear(); this.media.clear(); }
}
